import { useAuth0 } from '@auth0/auth0-react'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import { Box, IconButton } from '@mui/material'
import {
  IconCalendarCheck,
  IconPhone,
  IconRoute,
  IconUsersGroup,
} from '@tabler/icons-react'
import React, { useEffect, useRef, useState } from 'react'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import { Autoplay, EffectFade, Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Swiper as SwiperType } from 'swiper/types'
import { DEFAULT_CARD_IMAGE } from '../../helpers/static-constants'
import useAppUtilities from '../../hooks/useAppUtilities'
import { useAppSelector } from '../../store'
import { selectSavedRestaurantIds } from '../../store/profileSlice'
import { favoriteRestaurant } from '../../store/restaurantSlice'
import { logEvent } from '../../analytics/analytics'
import { persistAnonSave } from '../../services/rewardsService'
import { addAnonSavedAlias, isAnonSaved } from '../../helpers/anon-saves'
import {
  buildCallHref,
  buildDirectionsUrl,
  buildReservationUrl,
  hasDirections,
  hasReservation,
} from '../../helpers/restaurant-actions'
import { ROUTES } from '../../helpers/routes-helper'
import {
  RestaurantCardActionBarActions,
  RestaurantCardActionBarContainer,
  RestaurantCardActionBarLeft,
  RestaurantCardImgSectionCircularProgress,
  RestaurantCardImgSectionContainer,
  RestaurantCardImgSectionIconBookmark,
  RestaurantCardImgSectionIconBookmarkFilled,
  RestaurantCardImgSectionIconBookmarkFilledContainer,
  RestaurantCardImgSectionSkeletonContainer,
  RestaurantCardImgSectionTagChipContainer,
  RestaurantCardInfoContainer,
  RestaurantCardInfoDescriptionDistanceTypography,
  RestaurantCardInfoDescriptionIconPointFilled,
  RestaurantCardInfoTitleContainer,
  RestaurantCardInfoTitleIconRosetteDiscountCheck,
  RestaurantCardInfoTitleTypography,
  RestaurantCardInfoViewMenuTypography,
  RestaurantCardMatchQualityContainer,
  RestaurantCardMatchQualityTypography,
} from '../styled/restaurant/RestaurantCard.styled'
import ImgIcon from '../ui/ImgIcon'
import TagChip, { IMatchData } from '../ui/TagChip'
import RestaurantShare from '../../pages/eater/restaurant/components/RestaurantShare'
import { usePlannerMode } from '../../context/PlannerModeContext'

interface Data {
  percentage: string
  title?: string
  matchData?: IMatchData[]
}

export interface RestaurantCardProps {
  id: string
  title: string
  subTitle: string
  imgList: string[]
  isVerified: boolean
  alias?: string
  distance?: string
  tagChipData?: Data[]
  tagChipBgColor?: 'orange' | 'liteOrange' | 'white'
  recommendationSummary?: string
  recommendationId?: string
  searchId?: string
  /**
   * Model version that produced this card's recommendation. Threaded through so the save-path
   * `recommendation_accepted` (a high-intent positive label) carries model_version, matching the
   * click/view/reject paths emitted by the card containers. Absent on non-reco surfaces.
   */
  modelVersion?: string
  /**
   * Show the match chip even for anonymous users. The main app gates the match behind login as a
   * conversion lever, but group dining sessions are anonymous-first and the "Group match" is a
   * shared aggregate (not personal), so it's surfaced to everyone there.
   */
  matchAlwaysVisible?: boolean
  /**
   * Contact/location fields powering the always-visible terminal-action bar (Directions / Call /
   * Share / Reserve). All optional — a surface omits what its data lacks and the card hides the
   * corresponding action gracefully (e.g. no phone -> no Call). None of these actions require auth.
   */
  phone?: string
  latitude?: number
  longitude?: number
  placeId?: string
  address?: string
  /**
   * Booking hand-off fields for the Reserve action — the dedicated reservation page (preferred) and
   * the restaurant's website (fallback), mirroring the detail page's `reservationUrl ?? website`. No
   * URL from either -> the Reserve action hides.
   */
  reservationUrl?: string
  website?: string
  /** Passed through to the embedded RestaurantShare for its caption/hashtags. Blanks tolerated. */
  cuisine?: string
  city?: string
  /** Rank of this card in its list, threaded onto planner-add for ranked-choice attribution. */
  rankPosition?: number
  /**
   * The browsing surface this card is rendered on ('home', 'search', 'dine_session', ...). Recorded
   * against a planner pick so we can see WHERE people curate from. Purely attribution.
   */
  plannerSourceSurface?: string
  /**
   * The planner this card belongs to, when it is rendered inside one (the dine surface). Threaded
   * onto every terminal action so restaurant decisions made from a planner are attributable to it —
   * card actions otherwise fire from a shared component with no session context.
   */
  plannerShareToken?: string
}

const RestaurantCard: React.FC<RestaurantCardProps> = ({
  id,
  title,
  imgList,
  distance,
  tagChipData,
  isVerified,
  alias = '',
  tagChipBgColor = 'orange',
  recommendationSummary,
  recommendationId,
  searchId,
  modelVersion,
  matchAlwaysVisible = false,
  phone,
  latitude,
  longitude,
  placeId,
  address,
  reservationUrl,
  website,
  cuisine,
  city,
  rankPosition,
  plannerSourceSurface,
  plannerShareToken,
}) => {
  const { dispatch, isMediumScreen, theme } = useAppUtilities()
  const { isAuthenticated } = useAuth0()
  const saveRestaurants = useAppSelector(selectSavedRestaurantIds)
  const [isSaved, setIsSaved] = useState(false)
  // Anonymous saves render as saved too - device-local aliases mirroring the backend anon record.
  const [anonSaved, setAnonSaved] = useState(() => isAnonSaved(alias))
  const [staticLoader, setStaticLoader] = useState(false)
  const [loadingImage, setLoadingImage] = useState(true)
  const [imageError, setImageError] = useState(false)
  const swiperRef = useRef<SwiperType | null>(null)

  // Terminal actions — anonymous-friendly (no auth gate). Each stops propagation so a tap never
  // triggers the enclosing card's navigate-to-detail. Share is the shared RestaurantShare control
  // (Facebook/X/LinkedIn/Instagram/copy), reused from the detail page.
  const directionsInput = { latitude, longitude, placeId, address }
  const showDirections = hasDirections(directionsInput)
  const callHref = buildCallHref(phone)
  // Reserve is a referral/intent (Resactly can't see completion on the third-party destination) —
  // it opens the restaurant's reservation page, or its website as a fallback. Hidden when neither.
  const reservationInput = { reservationUrl, website }
  const showReserve = hasReservation(reservationInput)
  // A PATH (not a full URL): RestaurantShare prepends the canonical resactly.com origin itself.
  const shareUrlPath = `${ROUTES.RESTAURANT_DETAILS}/${alias}/details`

  // Planner mode. Undefined outside the provider (tests, isolated harnesses) — the action simply
  // doesn't render, so ordinary browsing is byte-for-byte unchanged when no planner is involved.
  const planner = usePlannerMode()
  const onPlanner = planner?.isOnPlanner(alias) ?? false
  // The planner action needs an alias to key the pick — hide it on the rare alias-less card.
  const showPlan = !!planner && !!alias
  // A card action taken inside a planner (dine surface) or while curating one is attributed to that
  // planner. This is the share_token that was missing from every dine-surface card action.
  const plannerToken = plannerShareToken ?? (planner?.active ? planner.shareToken : null)

  const actionEventProps = {
    restaurant_id: id,
    recommendation_id: recommendationId,
    search_id: searchId,
    surface: 'restaurant_card',
    ...(plannerToken ? { share_token: plannerToken } : {}),
    ...(planner?.active ? { planner_mode: true } : {}),
  }

  const handlePlan = (e: React.MouseEvent): void => {
    e.stopPropagation()
    // Anon-safe like every other terminal action: no auth wall, no signup gate. The first tap
    // creates the planner (once) and enters planner mode; later taps add/remove.
    void planner?.togglePlannerRestaurant({
      alias,
      restaurantId: id,
      name: title,
      latitude,
      longitude,
      recommendationId,
      searchId,
      rankPosition,
      sourceSurface: plannerSourceSurface ?? 'restaurant_card',
    })
  }

  const handleDirections = (e: React.MouseEvent): void => {
    e.stopPropagation()
    logEvent('card_directions_tapped', actionEventProps)
    if (typeof window !== 'undefined') {
      window.open(buildDirectionsUrl(directionsInput), '_blank', 'noopener')
    }
  }

  const handleCall = (e: React.MouseEvent): void => {
    e.stopPropagation()
    logEvent('card_call_tapped', actionEventProps)
    // Navigation happens via the anchor's href (tel:) — no auth gate. Do NOT preventDefault here or
    // the dialer never opens.
  }

  const handleReserve = (e: React.MouseEvent): void => {
    e.stopPropagation()
    logEvent('card_reserve_tapped', actionEventProps)
    const reserveUrl = buildReservationUrl(reservationInput)
    if (reserveUrl && typeof window !== 'undefined') {
      window.open(reserveUrl, '_blank', 'noopener')
    }
  }

  const isLogoFallback =
    !imgList[0] || imgList[0] === DEFAULT_CARD_IMAGE || imgList[0] === ''
  const showPlaceholder = imageError || isLogoFallback

  useEffect(() => {
    if (swiperRef.current?.autoplay) {
      swiperRef.current.autoplay.stop()
    }
  }, [])

  useEffect(() => {
    // anonSaved/isAuthenticated/alias must be deps: the anon tap flips anonSaved and the icon has
    // to follow immediately, and alias can arrive after mount (isAnonSaved covers that late read).
    setIsSaved(
      (saveRestaurants?.includes(id ?? '') ?? false) ||
        (!isAuthenticated && (anonSaved || isAnonSaved(alias))),
    )
  }, [saveRestaurants, id, isAuthenticated, anonSaved, alias])

  const handleMouseEnter = (): void => {
    swiperRef.current?.autoplay.start()
  }

  const handleMouseLeave = (): void => {
    swiperRef.current?.autoplay.stop()
  }

  const onClickBookmark = async (e): Promise<void> => {
    e.stopPropagation()

    // Terminal-action-bar tap signal — fires for both the image-overlay bookmark and the action-bar
    // Save, for anon and authed alike (alongside the existing save-funnel / restaurant_saved events).
    logEvent('card_save_tapped', actionEventProps)

    // Anonymous: the save SUCCEEDS immediately — no login gate. persistAnonSave is the durable
    // record (a backend GuestEngagementAction keyed to the never-rotated anon_id, deduped per
    // restaurant); the points claim promotes it into a real favorite on signup/login. The old
    // gate routed to signup with the intent in sessionStorage, which the OAuth redirect destroyed
    // in in-app WebViews — ~40 intents/month produced ~1 completed save.
    if (!isAuthenticated) {
      if (anonSaved || isAnonSaved(alias)) {
        return // already anon-saved; no anon unsave (the backend record is points-bearing)
      }
      // save_intent keeps firing for funnel continuity with the gated era; with anon-first it is
      // near-synonymous with the anon success and is slated for retirement.
      logEvent('save_intent', {
        restaurant_id: id,
        recommendation_id: recommendationId,
        search_id: searchId,
        screen_name: 'restaurant_card',
      })
      void persistAnonSave(id, alias)
      addAnonSavedAlias(alias)
      setAnonSaved(true)
      // Canonical success event, both auth states — auth_state distinguishes them.
      logEvent('restaurant_saved', {
        restaurant_id: id,
        recommendation_id: recommendationId,
        search_id: searchId,
        auth_state: 'anon',
        screen_name: 'restaurant_card',
      })
      return
    }

    setStaticLoader(true)
    const wasSaved = isSaved
    const result = await dispatch(
      favoriteRestaurant({ alias: alias ?? '', name: title }),
    )
    setStaticLoader(false)
    // The optimistic update in the reducer handles immediate UI feedback.
    if (favoriteRestaurant.fulfilled.match(result)) {
      if (wasSaved) {
        logEvent('restaurant_unsaved', {
          restaurant_id: id,
          screen_name: 'restaurant_card',
        })
      } else {
        logEvent('restaurant_saved', {
          auth_state: 'authed',
          restaurant_id: id,
          recommendation_id: recommendationId,
          search_id: searchId,
          screen_name: 'restaurant_card',
        })
        // A save from a reco card is a high-intent acceptance — label it so retraining sees more
        // than the weak action:'click' signal (only when a recommendation is actually in scope).
        if (recommendationId) {
          logEvent('recommendation_accepted', {
            recommendation_id: recommendationId,
            restaurant_id: id,
            action: 'save',
            model_version: modelVersion,
            screen_name: 'restaurant_card',
          })
        }
      }
    }
  }

  return (
    <div>
      <RestaurantCardImgSectionContainer
        id="imgSection"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="restaurant-card-swipe">
          {showPlaceholder ? (
            <Box
              sx={{
                height: isMediumScreen ? '182px' : '200px',
                width: '100%',
                borderRadius: '20px',
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.grey[100],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <StorefrontOutlinedIcon
                sx={{ fontSize: 40, color: theme.palette.grey[400] }}
              />
            </Box>
          ) : (
            <Swiper
              spaceBetween={30}
              centeredSlides={true}
              effect={imgList.length > 1 ? 'fade' : undefined}
              loop={imgList.length > 1}
              autoplay={
                imgList.length > 1
                  ? {
                      delay: 100,
                      disableOnInteraction: false,
                    }
                  : undefined
              }
              pagination={imgList.length > 1 ? { clickable: true } : undefined}
              grabCursor={imgList.length > 1}
              modules={[Autoplay, Pagination, Navigation, EffectFade]}
              className="mySwiper"
              onSwiper={(swiper) => {
                swiperRef.current = swiper
              }}
            >
              {imgList.map((img: string, index: number) => (
                <SwiperSlide
                  key={index}
                  virtualIndex={index}
                  style={{
                    background: 'transparent',
                  }}
                >
                  {loadingImage && (
                    <RestaurantCardImgSectionSkeletonContainer />
                  )}
                  <img
                    className="no-select"
                    src={img}
                    alt={`Resactly | ${title}`}
                    style={{
                      borderRadius: '20px',
                      objectFit: 'cover',
                      height: isMediumScreen ? '182px' : '200px',
                      display: !loadingImage ? 'block' : 'none',
                      filter: 'brightness(80%) saturate(115%)',
                    }}
                    onLoad={() => setLoadingImage(false)}
                    onError={() => {
                      setLoadingImage(false)
                      setImageError(true)
                    }}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          )}
        </div>
        <RestaurantCardImgSectionTagChipContainer>
          {(isAuthenticated || matchAlwaysVisible) &&
            Array.isArray(tagChipData) &&
            tagChipData.length > 0 && (
              <TagChip data={tagChipData} bgColor={tagChipBgColor} />
            )}
        </RestaurantCardImgSectionTagChipContainer>
        <RestaurantCardImgSectionIconBookmarkFilledContainer
          id={isMediumScreen || isSaved ? '' : 'saveBtn'}
        >
          <ImgIcon
            onClick={(evt) => {
              void onClickBookmark(evt)
            }}
            alt="bookmark"
            iconElement={
              staticLoader ? (
                <RestaurantCardImgSectionCircularProgress />
              ) : isSaved ? (
                <RestaurantCardImgSectionIconBookmarkFilled />
              ) : (
                <RestaurantCardImgSectionIconBookmark />
              )
            }
            wrapperStyle={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              height: '30px',
              width: '30px',
              boxShadow: `0px 0px 4px 0px #00000040`,
            }}
          />
        </RestaurantCardImgSectionIconBookmarkFilledContainer>
      </RestaurantCardImgSectionContainer>

      <RestaurantCardInfoContainer>
        <RestaurantCardInfoTitleContainer>
          <RestaurantCardInfoTitleTypography>
            {title}
          </RestaurantCardInfoTitleTypography>
          {isVerified && (
            <Box>
              <RestaurantCardInfoTitleIconRosetteDiscountCheck />
            </Box>
          )}
        </RestaurantCardInfoTitleContainer>
        {recommendationSummary && isAuthenticated && (
          <RestaurantCardMatchQualityContainer>
            <RestaurantCardMatchQualityTypography>
              {recommendationSummary}
            </RestaurantCardMatchQualityTypography>
          </RestaurantCardMatchQualityContainer>
        )}
      </RestaurantCardInfoContainer>

      {/* Always-visible terminal-action row. LEFT: the "View menu →" tap-in cue (+ distance) — these
          clicks intentionally bubble so the card still navigates to the detail page. RIGHT: the
          intent actions (Reserve / Directions / Call / Share), all anonymous-friendly (no auth gate). The
          actions container stops click + mousedown propagation so a tap on a button (or the gap
          between them) never triggers the surface wrapper's navigate-to-detail. Save is intentionally
          NOT here — the always-visible image-overlay bookmark above is the single Save. */}
      <RestaurantCardActionBarContainer>
        <RestaurantCardActionBarLeft>
          <RestaurantCardInfoViewMenuTypography>
            View menu →
          </RestaurantCardInfoViewMenuTypography>
          {distance && (
            <>
              <RestaurantCardInfoDescriptionIconPointFilled />
              <RestaurantCardInfoDescriptionDistanceTypography>
                {distance} Mi
              </RestaurantCardInfoDescriptionDistanceTypography>
            </>
          )}
        </RestaurantCardActionBarLeft>
        <RestaurantCardActionBarActions
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {showPlan && (
            <IconButton
              size="small"
              aria-label={
                onPlanner ? 'Remove from planner' : 'Plan with friends'
              }
              aria-pressed={onPlanner}
              onClick={handlePlan}
              disabled={planner?.busy}
              sx={{
                color: onPlanner
                  ? theme.palette.primary.main
                  : theme.palette.grey[700],
              }}
            >
              <IconUsersGroup
                size={20}
                stroke={onPlanner ? 2.25 : 1.75}
              />
            </IconButton>
          )}
          {showReserve && (
            <IconButton
              size="small"
              aria-label="Reserve"
              onClick={handleReserve}
              sx={{ color: theme.palette.grey[700] }}
            >
              <IconCalendarCheck size={20} stroke={1.75} />
            </IconButton>
          )}
          {showDirections && (
            <IconButton
              size="small"
              aria-label="Directions"
              onClick={handleDirections}
              sx={{ color: theme.palette.grey[700] }}
            >
              <IconRoute size={20} stroke={1.75} />
            </IconButton>
          )}
          {callHref && (
            <IconButton
              size="small"
              component="a"
              href={callHref}
              aria-label="Call"
              onClick={handleCall}
              sx={{ color: theme.palette.grey[700] }}
            >
              <IconPhone size={20} stroke={1.75} />
            </IconButton>
          )}
          <RestaurantShare
            restaurantId={id}
            title={title}
            imgUrl={imgList[0] ?? ''}
            description=""
            url={shareUrlPath}
            cuisine={cuisine ?? ''}
            city={city ?? ''}
            onOpen={() => logEvent('card_share_tapped', actionEventProps)}
            suppressHelmet
          />
        </RestaurantCardActionBarActions>
      </RestaurantCardActionBarContainer>
    </div>
  )
}

export default RestaurantCard
